/* eslint-disable max-lines */
import { UnifiedAlarm } from '@hakimo-ui/hakimo/types';
import { CameraAlarmTracker } from '../types';
import {
  areCameraAlarmTrackerMapsEqual,
  areSetsEqual,
  getDefaultCameraAlarmTracker,
} from './util';

interface WindowBounds {
  start: number;
  end: number;
}
interface PreloaderConfig {
  showWindowDurationInSec: number;
  preloadWindowDurationInSec: number;
}

export interface PreloaderState {
  showCameraIds: Set<string>;
  preloadCameraIds: Set<string>;
  camToAlarmTracker: Map<string, CameraAlarmTracker>;
}

export class AlarmPreloader {
  private showWindow: WindowBounds;
  private preloadWindow: WindowBounds;
  private alarms: UnifiedAlarm[];
  private currentShowAlarmIds = new Set<string>();
  // Show-window alarms the playhead has actually reached (start <= seeker).
  // Buffer-readiness gates on THIS set, not the whole 10s look-ahead show
  // window: an unbuffered video that won't play for several more seconds must
  // preload in the background, not pause the seeker mid-play. If it still
  // isn't ready when the playhead reaches its start, the due set changes at
  // that moment and the gate pauses there — honest, boundary-aligned buffering.
  private currentDueAlarmIds = new Set<string>();
  private currentShowCameraIds = new Set<string>();
  private progressListeners: Array<() => void> = [];
  private readyListeners: Array<() => void> = [];
  private notReadyListeners: Array<() => void> = [];
  private currentShowCameraIdChangeListeners: Array<() => void> = [];
  private camToAlarmTrackerListeners: Array<() => void> = [];
  private seekerTime = 0;

  private camToAlarmTracker = new Map<string, CameraAlarmTracker>();

  private alarmToLoadState = new Map<string, boolean>();
  private preloadCameraIds = new Set<string>();
  private preloadCameraIdChangeListeners: Array<() => void> = [];
  private stateChangeListeners: Array<(state: PreloaderState) => void> = [];

  constructor(initialAlarms: UnifiedAlarm[], private config: PreloaderConfig) {
    this.alarms = initialAlarms;
    this.showWindow = { start: 0, end: config.showWindowDurationInSec };
    this.preloadWindow = { start: 0, end: config.preloadWindowDurationInSec };
    this.updateWindows();
  }

  updateAlarms(newAlarms: UnifiedAlarm[]) {
    this.alarms = newAlarms;
    // Recompute windows immediately so late-arriving alarms (neighbour fetch)
    // enter the show/preload sets now — mounting their players while the
    // initial buffer still holds — instead of on the first tick after playback
    // starts, where an unbuffered due alarm would pause the seeker mid-play.
    this.updateWindows();
  }

  onProgress(cb: () => void) {
    this.progressListeners.push(cb);
  }
  onBufferReady(cb: () => void) {
    this.readyListeners.push(cb);
  }
  onBufferNotReady(cb: () => void) {
    this.notReadyListeners.push(cb);
  }
  updateAlarmLoadState(alarmId: string, isReady: boolean) {
    this.alarmToLoadState.set(alarmId, isReady);
    const allReady = [...this.currentDueAlarmIds].every(
      (id) => this.alarmToLoadState.get(id) === true
    );
    if (allReady) {
      this.emitBufferReady();
    }
  }

  onCurrentShowCameraIdsChange(cb: () => void) {
    this.currentShowCameraIdChangeListeners.push(cb);
  }

  onPreloadCameraIdsChange(cb: () => void) {
    this.preloadCameraIdChangeListeners.push(cb);
  }

  onCamToAlarmTrackerChange(cb: () => void) {
    this.camToAlarmTrackerListeners.push(cb);
  }

  getCurrentShowCameraIds() {
    return this.currentShowCameraIds;
  }

  getCurrentShowAlarmIds() {
    return this.currentShowAlarmIds;
  }

  getCurrentPreloadCameraIds() {
    return this.preloadCameraIds;
  }

  getCamToAlarmTracker() {
    return this.camToAlarmTracker;
  }

  getState(): PreloaderState {
    return {
      showCameraIds: this.currentShowCameraIds,
      preloadCameraIds: this.preloadCameraIds,
      camToAlarmTracker: this.camToAlarmTracker,
    };
  }

  onStateChange(cb: (state: PreloaderState) => void) {
    this.stateChangeListeners.push(cb);
  }

  // time in ms
  moveTo(timeSec: number) {
    const { showWindowDurationInSec, preloadWindowDurationInSec } = this.config;
    const newShow: WindowBounds = {
      start: timeSec,
      end: timeSec + showWindowDurationInSec * 1000,
    };
    const newPre: WindowBounds = {
      start: timeSec,
      end: timeSec + preloadWindowDurationInSec * 1000,
    };

    this.showWindow = newShow;
    this.preloadWindow = newPre;
    this.seekerTime = timeSec;
    this.updateWindows();
  }

  private updateWindows() {
    // Determine showWindow alarms
    const allShowAlarmIds = new Set<string>();
    const allDueAlarmIds = new Set<string>();
    const allShowCameraIds = new Set<string>();
    const allPreloadCameraIds = new Set<string>();

    const newCamToAlarmTracker = new Map();

    for (const alarm of this.alarms) {
      const camId = alarm.cameraId;
      const cameraAlarmTracker: CameraAlarmTracker =
        newCamToAlarmTracker.get(camId) || getDefaultCameraAlarmTracker();

      // alarms which falls in show window
      if (
        alarm.alarmEndTime >= this.showWindow.start &&
        alarm.alarmStartTime < this.showWindow.end
      ) {
        cameraAlarmTracker.showAlarmIds.add(alarm.id);
        allShowAlarmIds.add(alarm.id);
        allShowCameraIds.add(alarm.cameraId);
        // Only video alarms gate the buffer: they report readiness via
        // canplay. Frames alarms never do — including them would hold the
        // seeker forever.
        if (
          alarm.alarmStartTime <= this.seekerTime &&
          alarm.media?.type === 'video'
        ) {
          allDueAlarmIds.add(alarm.id);
        }
      }
      // alarms which falls in preload window
      if (
        alarm.alarmEndTime >= this.preloadWindow.start &&
        alarm.alarmStartTime < this.preloadWindow.end
      ) {
        cameraAlarmTracker.preloadAlarmIds.add(alarm.id);
        allPreloadCameraIds.add(alarm.cameraId);
      }
      // alarms which intersect with seeker time
      if (
        alarm.alarmStartTime / 1000 <= this.seekerTime / 1000 &&
        alarm.alarmEndTime / 1000 >= this.seekerTime / 1000
      ) {
        cameraAlarmTracker.currentAlarmId = alarm.id;
      }

      if (alarm.alarmEndTime < this.seekerTime) {
        this.alarmToLoadState.set(alarm.id, false);
      }

      newCamToAlarmTracker.set(camId, cameraAlarmTracker);
    }

    newCamToAlarmTracker.forEach((value, key) => {
      const { showAlarmIds } = value;
      newCamToAlarmTracker.set(key, {
        ...value,
        isCamShownCurrently: showAlarmIds.size > 0,
      });
    });

    let hasStateChanged = false;

    if (!areSetsEqual(this.currentShowAlarmIds, allShowAlarmIds)) {
      this.currentShowAlarmIds = allShowAlarmIds;
    }
    if (!areSetsEqual(this.currentDueAlarmIds, allDueAlarmIds)) {
      this.currentDueAlarmIds = allDueAlarmIds;
      this.checkIfAllDueAlarmsAreLoaded();
    }
    if (!areSetsEqual(this.currentShowCameraIds, allShowCameraIds)) {
      this.currentShowCameraIds = allShowCameraIds;
      this.emitShowCameraIdsChange();
      hasStateChanged = true;
    }
    if (!areSetsEqual(this.preloadCameraIds, allPreloadCameraIds)) {
      this.preloadCameraIds = allPreloadCameraIds;
      this.emitPreloadCameraIdsChange();
      hasStateChanged = true;
    }

    if (
      !areCameraAlarmTrackerMapsEqual(
        newCamToAlarmTracker,
        this.camToAlarmTracker
      )
    ) {
      this.camToAlarmTracker = newCamToAlarmTracker;
      this.emitCamToAlarmTrackerChange();
      hasStateChanged = true;
    }

    // Emit consolidated state change for batched React updates
    if (hasStateChanged) {
      this.emitStateChange();
    }
  }

  private emitStateChange() {
    const state = this.getState();
    this.stateChangeListeners.forEach((cb) => cb(state));
  }

  private checkIfAllDueAlarmsAreLoaded() {
    const allDueAlarmIds = [...this.currentDueAlarmIds];
    const allReady = allDueAlarmIds.every(
      (id) => this.alarmToLoadState.get(id) === true
    );
    if (allReady) {
      this.emitBufferReady();
    } else {
      this.emitBufferNotReady();
    }
  }

  private emitBufferReady() {
    this.readyListeners.forEach((cb) => cb());
  }

  private emitBufferNotReady() {
    this.notReadyListeners.forEach((cb) => cb());
  }

  private emitShowCameraIdsChange() {
    this.currentShowCameraIdChangeListeners.forEach((cb) => cb());
  }

  private emitPreloadCameraIdsChange() {
    this.preloadCameraIdChangeListeners.forEach((cb) => cb());
  }

  private emitCamToAlarmTrackerChange() {
    this.camToAlarmTrackerListeners.forEach((cb) => cb());
  }
}
